"use client";
import React, { useState, useMemo, useEffect } from "react";
import {
  TrendingUp, ShoppingCart, Users, Package, IndianRupee, Receipt,
  BarChart3, Download, Calendar, ChevronDown, ArrowUpRight,
  ArrowDownRight, Minus, Filter, RefreshCcw, FileText, Star,
  CheckCircle, XCircle, Clock, AlertCircle, Truck, CreditCard,
} from "lucide-react";
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line, PieChart, Pie,
  Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ComposedChart,
} from "recharts";
import { useOrders }      from "../context/OrdersContext";
import { useCustomers }   from "../context/CustomersContext";
import { useProducts }    from "../context/ProductsContext";
import { useExpenses }    from "../context/ExpenseContext";
import { useCategories }  from "../context/CategoryContext";
import DayEndReport       from "./DayEndReport";

// ─── Constants ────────────────────────────────────────────────────────────────
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const COLORS  = ["#0F766E","#14B8A6","#7C3AED","#F59E0B","#3B82F6","#EC4899","#EF4444","#10B981"];

const REPORT_TABS = [
  { id:"dayend",    label:"Day End",   icon:Calendar,     color:"#0D5F58" },
  { id:"sales",     label:"Sales",     icon:TrendingUp,   color:"#0F766E" },
  { id:"orders",    label:"Orders",    icon:ShoppingCart, color:"#3B82F6" },
  { id:"customers", label:"Customers", icon:Users,        color:"#7C3AED" },
  { id:"products",  label:"Products",  icon:Package,      color:"#F59E0B" },
  { id:"financial", label:"Financial", icon:IndianRupee,  color:"#10B981" },
  { id:"expenses",  label:"Expenses",  icon:Receipt,      color:"#EF4444" },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt  = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");
const fmtK = (n) => {
  if (n >= 10_00_000) return `₹${(n / 10_00_000).toFixed(1)}Cr`;
  if (n >= 1_00_000)  return `₹${(n / 1_00_000).toFixed(1)}L`;
  if (n >= 1_000)     return `₹${(n / 1_000).toFixed(0)}k`;
  return `₹${Math.round(n)}`;
};

// Truncate text to N chars with ellipsis — keeps KpiCard sub text responsive
const trunc = (str, max = 16) => {
  if (!str) return "";
  const s = String(str);
  return s.length > max ? s.slice(0, max).trimEnd() + "…" : s;
};

function dateInRange(dateStr, from, to) {
  if (!dateStr) return true;
  const d = new Date(dateStr.slice(0, 10));
  if (from && d < new Date(from)) return false;
  if (to   && d > new Date(to))   return false;
  return true;
}

function getMonthKey(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2,"0")}`;
}

function monthLabel(key) {
  const [, m] = key.split("-");
  return MONTHS[parseInt(m, 10) - 1];
}

// ─── Shared UI ────────────────────────────────────────────────────────────────
function KpiCard({ label, value, sub, icon: Icon, iconBg, iconColor, trend, trendLabel }) {
  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-2 shadow-sm hover:shadow-md transition-shadow min-w-0 overflow-hidden w-full">
      <div className="flex items-start justify-between mb-1.5">
        <div
          className="w-7 h-7 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: iconBg }}
        >
          <Icon className="w-3 h-3" style={{ color: iconColor }} />
        </div>
        {trend != null && (
          <div className={`flex items-center gap-0.5 text-[9px] font-bold px-1 py-0.5 rounded-full flex-shrink-0 ml-1 max-w-[60px] overflow-hidden ${
            trend > 0 ? "bg-green-100 text-green-700" :
            trend < 0 ? "bg-red-100 text-red-600"    : "bg-gray-100 text-gray-500"
          }`}>
            {trend > 0 ? <ArrowUpRight className="w-2.5 h-2.5 flex-shrink-0" /> :
             trend < 0 ? <ArrowDownRight className="w-2.5 h-2.5 flex-shrink-0" /> :
             <Minus className="w-2.5 h-2.5 flex-shrink-0" />}
            <span className="truncate">{trendLabel || `${Math.abs(trend)}%`}</span>
          </div>
        )}
      </div>
      {/* Value: hard truncate at 10 chars so ₹4.1L never wraps */}
      <p className="text-sm font-extrabold text-gray-900 tracking-tight truncate" title={String(value)}>
        {trunc(String(value), 10)}
      </p>
      {/* Label: uppercase tiny text, capped at 14 chars */}
      <p className="text-[9px] font-semibold text-gray-400 uppercase tracking-widest mt-0.5 truncate" title={label}>
        {trunc(label, 14)}
      </p>
      {/* Sub: descriptive line, capped at 16 chars */}
      {sub && (
        <p className="text-[9px] text-gray-500 mt-0.5 truncate" title={String(sub)}>
          {trunc(String(sub), 16)}
        </p>
      )}
    </div>
  );
}

function ChartCard({ title, icon: Icon, iconColor = "#0F766E", children, className = "" }) {
  return (
    <div className={`bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden ${className}`}>
      <div className="flex items-center gap-2 px-3 py-2.5 border-b border-gray-100">
        {Icon && (
          <div className="w-5 h-5 rounded-lg bg-teal-50 flex items-center justify-center flex-shrink-0">
            <Icon className="w-3 h-3" style={{ color: iconColor }} />
          </div>
        )}
        <span className="text-[11px] font-bold text-gray-800 truncate">{title}</span>
      </div>
      <div className="p-2.5 sm:p-3 md:p-4">{children}</div>
    </div>
  );
}

function ChartTip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-xl px-2.5 py-2 shadow-xl text-xs min-w-[110px] max-w-[180px]">
      {label && (
        <p className="font-bold text-gray-400 uppercase tracking-wider mb-1 text-[9px] truncate">
          {label}
        </p>
      )}
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-1.5 mb-1">
          <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: p.color || p.fill }} />
          <span className="text-gray-500 flex-1 truncate">{p.name}</span>
          <span className="font-bold text-gray-900 pl-1 flex-shrink-0">
            {typeof p.value === "number" &&
              (p.name?.toLowerCase().includes("rev")     ||
               p.name?.toLowerCase().includes("₹")      ||
               p.name?.toLowerCase().includes("amount")  ||
               p.name?.toLowerCase().includes("profit")  ||
               p.name?.toLowerCase().includes("expense"))
              ? fmtK(p.value)
              : p.value}
          </span>
        </div>
      ))}
    </div>
  );
}

function DataTable({ columns, rows, emptyMsg = "No data" }) {
  if (!rows.length) return (
    <div className="py-10 text-center text-gray-400 text-sm">{emptyMsg}</div>
  );
  return (
    <div className="overflow-x-auto rounded-xl border border-gray-200 -mx-1">
      <table className="w-full text-xs min-w-[480px]">
        <thead>
          <tr className="bg-teal-700 text-white">
            {columns.map((c, i) => (
              <th key={i} className={`px-3 py-2.5 text-[10px] font-semibold uppercase tracking-wider whitespace-nowrap ${c.right ? "text-right" : "text-left"}`}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((row, ri) => (
            <tr key={ri} className={`transition-colors hover:bg-teal-50/30 ${ri % 2 === 0 ? "bg-white" : "bg-gray-50/50"}`}>
              {columns.map((c, ci) => (
                <td key={ci} className={`px-3 py-2.5 whitespace-nowrap ${c.right ? "text-right" : ""} ${c.bold ? "font-semibold text-gray-900" : "text-gray-700"}`}>
                  {row[c.key] ?? "—"}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    delivered:  { bg:"#DCFCE7", color:"#15803D" },
    completed:  { bg:"#DCFCE7", color:"#15803D" },
    placed:     { bg:"#DBEAFE", color:"#1E40AF" },
    pending:    { bg:"#FEF9C3", color:"#854D0E" },
    cancelled:  { bg:"#FEE2E2", color:"#991B1B" },
    refunded:   { bg:"#F3E8FF", color:"#6B21A8" },
    active:     { bg:"#DCFCE7", color:"#15803D" },
  };
  const s   = (status || "").toLowerCase();
  const cfg = map[s] || { bg:"#F3F4F6", color:"#6B7280" };
  return (
    <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold capitalize whitespace-nowrap"
      style={{ background:cfg.bg, color:cfg.color }}>
      {status || "—"}
    </span>
  );
}

// ─── Filter Bar ───────────────────────────────────────────────────────────────
// FIX: changed md:flex-row → sm:flex-row so it goes horizontal at 640px+
// This fixes the stacking issue at 760px viewport
function FilterBar({ from, to, setFrom, setTo, onReset, extra }) {
  return (
    <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2 bg-white border border-gray-200 rounded-xl px-3 py-2.5 shadow-sm">

      {/* Date range group */}
      <div className="flex flex-wrap items-center gap-1.5 flex-1 min-w-0">
        <Filter className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
        <span className="text-gray-400 text-[10px] font-semibold whitespace-nowrap">FROM</span>
        {/* FIX: fixed width instead of w-full so dates stay inline at 760px */}
        <input
          type="date"
          value={from}
          onChange={e => setFrom(e.target.value)}
          className="border border-gray-200 rounded-lg px-2 py-1 text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-500 w-[110px]"
        />
        <span className="text-gray-400 text-[10px] font-semibold whitespace-nowrap">TO</span>
        <input
          type="date"
          value={to}
          onChange={e => setTo(e.target.value)}
          className="border border-gray-200 rounded-lg px-2 py-1 text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-500 w-[110px]"
        />
      </div>

      {/* Controls group: extra filter + reset + export */}
      <div className="flex items-center gap-2 flex-wrap flex-shrink-0">
        {extra && (
          <div className="flex-shrink-0 max-w-[140px] min-w-0">
            {extra}
          </div>
        )}

        {(from || to) && (
          <button
            onClick={onReset}
            className="flex items-center gap-1 text-xs text-gray-500 hover:text-red-500 border border-gray-200 rounded-lg px-2 py-1 hover:border-red-200 transition-colors whitespace-nowrap"
          >
            <RefreshCcw className="w-3 h-3" />
            <span>Reset</span>
          </button>
        )}

        <button className="flex items-center gap-1 text-xs text-teal-600 font-semibold border border-teal-200 rounded-lg px-2 py-1 hover:bg-teal-50 transition-colors whitespace-nowrap">
          <Download className="w-3 h-3" />
          <span className="hidden sm:inline">Export CSV</span>
          <span className="sm:hidden">CSV</span>
        </button>
      </div>
    </div>
  );
}

// ─── Chart wrapper ────────────────────────────────────────────────────────────
function ChartWrap({ height = 200, children }) {
  return (
    <div style={{ width: "100%", minHeight: height }}>
      <ResponsiveContainer width="100%" height={height}>
        {children}
      </ResponsiveContainer>
    </div>
  );
}

// ─── SALES REPORT ─────────────────────────────────────────────────────────────
function SalesReport({ orders }) {
  const [from, setFrom] = useState("");
  const [to,   setTo]   = useState("");

  const filtered = useMemo(() =>
    orders.filter(o => dateInRange(o.date, from, to)),
  [orders, from, to]);

  const totalRevenue   = filtered.reduce((s, o) => s + (o.total || 0), 0);
  const totalOrders    = filtered.length;
  const avgOrderValue  = totalOrders ? totalRevenue / totalOrders : 0;
  const paidOrders     = filtered.filter(o => (o.payment_status || "").toLowerCase() === "paid");
  const paidRevenue    = paidOrders.reduce((s, o) => s + (o.total || 0), 0);

  const monthlyData = useMemo(() => {
    const map = {};
    filtered.forEach(o => {
      const k = getMonthKey(o.date);
      if (!k) return;
      if (!map[k]) map[k] = { m: monthLabel(k), revenue: 0, orders: 0, avg: 0 };
      map[k].revenue += o.total || 0;
      map[k].orders  += 1;
    });
    return Object.entries(map).sort(([a],[b]) => a.localeCompare(b))
      .map(([, v]) => ({ ...v, avg: v.orders ? Math.round(v.revenue / v.orders) : 0 }));
  }, [filtered]);

  const categoryData = useMemo(() => {
    const map = {};
    filtered.forEach(o => {
      const cat = o.category || o.product_name || "Other";
      map[cat] = (map[cat] || 0) + (o.total || 0);
    });
    return Object.entries(map).map(([name, revenue]) => ({ name, revenue: Math.round(revenue) }))
      .sort((a,b) => b.revenue - a.revenue).slice(0, 8);
  }, [filtered]);

  const paymentData = useMemo(() => {
    const map = {};
    filtered.forEach(o => {
      const pm = o.payment_method || o.paymentMethod || "Other";
      map[pm] = (map[pm] || 0) + (o.total || 0);
    });
    return Object.entries(map).map(([name, value]) => ({ name, value: Math.round(value) }))
      .sort((a,b) => b.value - a.value);
  }, [filtered]);

  const topOrders = useMemo(() =>
    [...filtered].sort((a,b) => (b.total||0) - (a.total||0)).slice(0, 10),
  [filtered]);

  const AXIS = { fontSize:10, fill:"#9CA3AF" };
  const GRID = { strokeDasharray:"2 8", stroke:"rgba(0,0,0,0.05)", vertical:false };

  return (
    <div className="space-y-4">
      <FilterBar from={from} to={to} setFrom={setFrom} setTo={setTo} onReset={() => { setFrom(""); setTo(""); }} />

      {/* FIX: sm:grid-cols-4 fires at 640px+ covering 760px viewport */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 w-full">
        <KpiCard label="Total Revenue"     value={fmtK(totalRevenue)}  icon={IndianRupee}  iconBg="#F0FDFA" iconColor="#0F766E" />
        <KpiCard label="Total Orders"      value={totalOrders}         icon={ShoppingCart} iconBg="#EFF6FF" iconColor="#1D4ED8" />
        <KpiCard label="Avg Order Value"   value={fmtK(avgOrderValue)} icon={TrendingUp}   iconBg="#F3E8FF" iconColor="#6B21A8"
          sub={`${totalOrders} orders`} />
        <KpiCard label="Collected Revenue" value={fmtK(paidRevenue)}   icon={CheckCircle}  iconBg="#DCFCE7" iconColor="#15803D"
          sub={`${paidOrders.length} paid`} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ChartCard title="Monthly Revenue Trend" icon={TrendingUp} className="lg:col-span-2">
          <ChartWrap height={200}>
            <ComposedChart data={monthlyData}>
              <defs>
                <linearGradient id="sRevGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0F766E" stopOpacity={0.18} />
                  <stop offset="100%" stopColor="#0F766E" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid {...GRID} />
              <XAxis dataKey="m" tick={AXIS} axisLine={false} tickLine={false} />
              <YAxis tick={AXIS} tickFormatter={fmtK} width={46} axisLine={false} tickLine={false} />
              <YAxis yAxisId="r" orientation="right" tick={AXIS} width={26} axisLine={false} tickLine={false} />
              <Tooltip content={<ChartTip />} />
              <Legend wrapperStyle={{ fontSize:10, paddingTop:6 }} />
              <Area type="monotone" dataKey="revenue" stroke="#0F766E" strokeWidth={2.5} fill="url(#sRevGrad)" name="Revenue" />
              <Line yAxisId="r" type="monotone" dataKey="orders" stroke="#F59E0B" strokeWidth={2} dot={false} name="Orders" />
            </ComposedChart>
          </ChartWrap>
        </ChartCard>

        <ChartCard title="Payment Methods" icon={CreditCard} iconColor="#7C3AED">
          <ChartWrap height={200}>
            <PieChart>
              <Pie data={paymentData} cx="50%" cy="42%" innerRadius={48} outerRadius={72}
                dataKey="value" paddingAngle={3} strokeWidth={0}>
                {paymentData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={v => fmtK(v)} />
              <Legend iconSize={8} wrapperStyle={{ fontSize:10 }} />
            </PieChart>
          </ChartWrap>
        </ChartCard>
      </div>

      <ChartCard title="Revenue by Category" icon={BarChart3}>
        <ChartWrap height={190}>
          <BarChart data={categoryData}>
            <CartesianGrid {...GRID} />
            <XAxis dataKey="name" tick={AXIS} axisLine={false} tickLine={false} angle={-15} textAnchor="end" height={40} />
            <YAxis tick={AXIS} tickFormatter={fmtK} width={48} axisLine={false} tickLine={false} />
            <Tooltip content={<ChartTip />} />
            <Bar dataKey="revenue" radius={[6,6,0,0]} name="Revenue">
              {categoryData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
            </Bar>
          </BarChart>
        </ChartWrap>
      </ChartCard>

      <ChartCard title="Top 10 Orders" icon={FileText}>
        <DataTable
          columns={[
            { key:"no",      label:"#" },
            { key:"id",      label:"Order ID",  bold:true },
            { key:"date",    label:"Date" },
            { key:"customer",label:"Customer" },
            { key:"method",  label:"Payment" },
            { key:"status",  label:"Status" },
            { key:"amount",  label:"Amount", right:true, bold:true },
          ]}
          rows={topOrders.map((o, i) => ({
            no:       i + 1,
            id:       trunc(o.orderNumber || o.id, 12),
            date:     o.date?.slice(0,10) || "—",
            customer: trunc(o.customer?.name || "—", 14),
            method:   trunc(o.paymentMethod || "—", 12),
            status:   <StatusPill status={o.rawStatus} />,
            amount:   fmt(o.total || 0),
          }))}
          emptyMsg="No orders in this period"
        />
      </ChartCard>
    </div>
  );
}

// ─── ORDERS REPORT ────────────────────────────────────────────────────────────
function OrdersReport({ orders }) {
  const [from, setFrom] = useState("");
  const [to,   setTo]   = useState("");
  const [statusF, setStatusF] = useState("all");

  const filtered = useMemo(() =>
    orders.filter(o => {
      const inRange = dateInRange(o.date, from, to);
      const matchS  = statusF === "all" || (o.rawStatus||"").toLowerCase() === statusF;
      return inRange && matchS;
    }),
  [orders, from, to, statusF]);

  const totalOrders = filtered.length;
  const delivered   = filtered.filter(o => /delivered|completed/i.test(o.rawStatus||"")).length;
  const cancelled   = filtered.filter(o => /cancelled|cancel/i.test(o.rawStatus||"")).length;
  const pending     = filtered.filter(o => /pending|placed/i.test(o.rawStatus||"")).length;
  const fulfillRate = totalOrders ? Math.round(delivered / totalOrders * 100) : 0;
  const cancelRate  = totalOrders ? Math.round(cancelled / totalOrders * 100) : 0;

  const statusBreakdown = useMemo(() => {
    const map = {};
    filtered.forEach(o => {
      const s = o.rawStatus || "Unknown";
      map[s] = (map[s] || 0) + 1;
    });
    return Object.entries(map).map(([name, value]) => ({ name, value })).sort((a,b) => b.value - a.value);
  }, [filtered]);

  const monthlyOrders = useMemo(() => {
    const map = {};
    filtered.forEach(o => {
      const k = getMonthKey(o.date);
      if (!k) return;
      if (!map[k]) map[k] = { m: monthLabel(k), total:0, delivered:0, cancelled:0, pending:0 };
      map[k].total += 1;
      if (/delivered|completed/i.test(o.rawStatus||"")) map[k].delivered += 1;
      else if (/cancelled/i.test(o.rawStatus||""))       map[k].cancelled += 1;
      else                                                map[k].pending   += 1;
    });
    return Object.entries(map).sort(([a],[b]) => a.localeCompare(b)).map(([,v]) => v);
  }, [filtered]);

  const orderTypeData = useMemo(() => {
    const map = {};
    filtered.forEach(o => {
      const t = o.orderTypeLabel || "Standard";
      map[t] = (map[t] || 0) + 1;
    });
    return Object.entries(map).map(([name, value]) => ({ name, value })).sort((a,b) => b.value - a.value);
  }, [filtered]);

  const AXIS = { fontSize:10, fill:"#9CA3AF" };
  const GRID = { strokeDasharray:"2 8", stroke:"rgba(0,0,0,0.05)", vertical:false };
  const uniqueStatuses = ["all", ...Array.from(new Set(orders.map(o => (o.rawStatus||"").toLowerCase()).filter(Boolean)))];

  return (
    <div className="space-y-4">
      <FilterBar from={from} to={to} setFrom={setFrom} setTo={setTo} onReset={() => { setFrom(""); setTo(""); setStatusF("all"); }}
        extra={
          <select value={statusF} onChange={e => setStatusF(e.target.value)}
            className="border border-gray-200 rounded-lg px-2 py-1 text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-500 capitalize w-full">
            {uniqueStatuses.map(s => <option key={s} value={s}>{s === "all" ? "All Statuses" : s}</option>)}
          </select>
        }
      />

      {/* FIX: sm:grid-cols-4 covers 760px */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 w-full">
        <KpiCard label="Total Orders"     value={totalOrders} icon={ShoppingCart} iconBg="#EFF6FF" iconColor="#1D4ED8" />
        <KpiCard label="Delivered"        value={delivered}   icon={Truck}        iconBg="#DCFCE7" iconColor="#15803D"
          trend={fulfillRate} trendLabel={`${fulfillRate}%`} />
        <KpiCard label="Cancelled"        value={cancelled}   icon={XCircle}      iconBg="#FEE2E2" iconColor="#991B1B"
          trend={cancelRate > 20 ? -1 : 0} trendLabel={`${cancelRate}%`} />
        <KpiCard label="Pending / Active" value={pending}     icon={Clock}        iconBg="#FEF9C3" iconColor="#854D0E" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ChartCard title="Monthly Order Volume" icon={BarChart3} className="lg:col-span-2">
          <ChartWrap height={200}>
            <BarChart data={monthlyOrders}>
              <CartesianGrid {...GRID} />
              <XAxis dataKey="m" tick={AXIS} axisLine={false} tickLine={false} />
              <YAxis tick={AXIS} axisLine={false} tickLine={false} width={24} />
              <Tooltip content={<ChartTip />} />
              <Legend wrapperStyle={{ fontSize:10, paddingTop:6 }} />
              <Bar dataKey="delivered" stackId="a" fill="#10B981" name="Delivered" />
              <Bar dataKey="pending"   stackId="a" fill="#F59E0B" name="Pending" />
              <Bar dataKey="cancelled" stackId="a" fill="#EF4444" name="Cancelled" radius={[4,4,0,0]} />
            </BarChart>
          </ChartWrap>
        </ChartCard>

  <ChartCard title="Status Breakdown" icon={AlertCircle} iconColor="#F59E0B">
  <ChartWrap height={280}>                         {/* was 200 — 20 legend items need room */}
    <PieChart margin={{ top: 12, right: 0, bottom: 0, left: 0 }}>
      <Pie
        data={statusBreakdown}
        cx="50%"
        cy="38%"                                  
        innerRadius={44}
        outerRadius={66}
        dataKey="value"
        paddingAngle={2}                            
        strokeWidth={0}
      >
        {statusBreakdown.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
      </Pie>
      <Tooltip />
      <Legend
        iconSize={7}
        wrapperStyle={{ fontSize: 9, lineHeight: '14px', maxHeight: 110, overflowY: 'auto' }}
      />
    </PieChart>
  </ChartWrap>
</ChartCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Order Types" icon={ShoppingCart}>
          <ChartWrap height={190}>
            <BarChart data={orderTypeData} layout="vertical">
              <CartesianGrid strokeDasharray="2 8" stroke="rgba(0,0,0,0.05)" horizontal={false} />
              <XAxis type="number" tick={AXIS} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="name" width={76} tick={AXIS} axisLine={false} tickLine={false} />
              <Tooltip content={<ChartTip />} />
              <Bar dataKey="value" fill="#0F766E" radius={[0,6,6,0]} name="Orders" />
            </BarChart>
          </ChartWrap>
        </ChartCard>

        <ChartCard title="Fulfillment Summary" icon={CheckCircle} iconColor="#15803D">
          <div className="space-y-3.5 py-1">
            {[
              { label:"Fulfillment Rate",  value:`${fulfillRate}%`, color:"#10B981", pct:fulfillRate },
              { label:"Cancellation Rate", value:`${cancelRate}%`,  color:"#EF4444", pct:cancelRate  },
              { label:"Pending Rate",      value:`${totalOrders ? Math.round(pending/totalOrders*100) : 0}%`, color:"#F59E0B", pct: totalOrders ? Math.round(pending/totalOrders*100) : 0 },
            ].map(item => (
              <div key={item.label}>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-gray-600 font-medium">{item.label}</span>
                  <span className="font-bold" style={{ color:item.color }}>{item.value}</span>
                </div>
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all" style={{ width:`${item.pct}%`, background:item.color }} />
                </div>
              </div>
            ))}
            <div className="pt-2.5 border-t border-gray-100 grid grid-cols-2 gap-2.5 text-center">
              <div className="bg-green-50 rounded-xl p-2.5">
                <p className="text-lg font-bold text-green-700">{delivered}</p>
                <p className="text-[10px] text-green-600">Completed</p>
              </div>
              <div className="bg-red-50 rounded-xl p-2.5">
                <p className="text-lg font-bold text-red-600">{cancelled}</p>
                <p className="text-[10px] text-red-500">Cancelled</p>
              </div>
            </div>
          </div>
        </ChartCard>
      </div>

      <ChartCard title="Recent Orders" icon={FileText}>
        <DataTable
          columns={[
            { key:"no",      label:"#" },
            { key:"id",      label:"Order ID", bold:true },
            { key:"date",    label:"Date" },
            { key:"customer",label:"Customer" },
            { key:"type",    label:"Type" },
            { key:"status",  label:"Status" },
            { key:"amount",  label:"Amount", right:true, bold:true },
          ]}
          rows={filtered.slice(0,15).map((o, i) => ({
            no:       i + 1,
            id:       trunc(o.orderNumber || o.id, 12),
            date:     o.date?.slice(0,10) || "—",
            customer: trunc(o.customer?.name || "—", 14),
            type:     trunc(o.orderTypeLabel || "Standard", 12),
            status:   <StatusPill status={o.rawStatus} />,
            amount:   fmt(o.total || 0),
          }))}
          emptyMsg="No orders in this period"
        />
      </ChartCard>
    </div>
  );
}

// ─── CUSTOMERS REPORT ─────────────────────────────────────────────────────────
function CustomersReport({ customers, orders }) {
  const [from, setFrom] = useState("");
  const [to,   setTo]   = useState("");

  const statsMap = useMemo(() => {
    const map = {};
    customers.forEach(c => { map[c.id] = { totalOrders:0, totalSpent:0 }; });
    orders.forEach(o => {
      const id = o.customer?.id;
      if (!id || !map[id]) return;
      map[id].totalOrders += 1;
      map[id].totalSpent  += o.total || 0;
    });
    return map;
  }, [customers, orders]);

  const filtered = useMemo(() =>
    customers.filter(c => dateInRange(c.registrationDate || c.createdAt, from, to)),
  [customers, from, to]);

  const active     = filtered.filter(c => c.status === "active").length;
  const inactive   = filtered.length - active;
  const totalRev   = filtered.reduce((s, c) => s + (statsMap[c.id]?.totalSpent || 0), 0);
  const avgRev     = filtered.length ? totalRev / filtered.length : 0;
  const withOrders = filtered.filter(c => (statsMap[c.id]?.totalOrders || 0) > 0).length;
  const repeatRate = filtered.length ? Math.round(withOrders / filtered.length * 100) : 0;
  const activeRate = filtered.length ? Math.round(active / filtered.length * 100) : 0;

  const monthlySignups = useMemo(() => {
    const map = {};
    filtered.forEach(c => {
      const k = getMonthKey(c.registrationDate || c.createdAt);
      if (!k) return;
      if (!map[k]) map[k] = { m: monthLabel(k), customers:0, revenue:0 };
      map[k].customers += 1;
      map[k].revenue   += statsMap[c.id]?.totalSpent || 0;
    });
    return Object.entries(map).sort(([a],[b]) => a.localeCompare(b)).map(([,v]) => v);
  }, [filtered, statsMap]);

  const statusDist = [
    { name:"Active",   value:active,   color:"#10B981" },
    { name:"Inactive", value:inactive, color:"#EF4444" },
  ].filter(x => x.value > 0);

  const topCustomers = useMemo(() =>
    filtered.map(c => ({ ...c, ...statsMap[c.id] }))
      .sort((a,b) => (b.totalSpent||0) - (a.totalSpent||0))
      .slice(0, 10),
  [filtered, statsMap]);

  const cityData = useMemo(() => {
    const map = {};
    filtered.forEach(c => {
      const city = c.address?.city || "Unknown";
      map[city] = (map[city] || 0) + 1;
    });
    return Object.entries(map).map(([name, value]) => ({ name, value }))
      .sort((a,b) => b.value - a.value).slice(0, 6);
  }, [filtered]);

  const AXIS = { fontSize:10, fill:"#9CA3AF" };
  const GRID = { strokeDasharray:"2 8", stroke:"rgba(0,0,0,0.05)", vertical:false };

  return (
    <div className="space-y-4">
      <FilterBar from={from} to={to} setFrom={setFrom} setTo={setTo} onReset={() => { setFrom(""); setTo(""); }} />

      {/* FIX: sm:grid-cols-4 covers 760px viewport */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 w-full">
        <KpiCard label="Total Customers"  value={filtered.length}  icon={Users}       iconBg="#F0FDFA" iconColor="#0F766E" />
        <KpiCard label="Active"           value={active}           icon={CheckCircle} iconBg="#DCFCE7" iconColor="#15803D"
          sub={`${activeRate}% active`} />
        <KpiCard label="Lifetime Revenue" value={fmtK(totalRev)}   icon={IndianRupee} iconBg="#FEF9C3" iconColor="#854D0E"
          sub={`Avg ${fmtK(avgRev)} each`} />
        <KpiCard label="Repeat Rate"      value={`${repeatRate}%`} icon={Star}        iconBg="#F3E8FF" iconColor="#6B21A8"
          sub={`${withOrders} with orders`} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ChartCard title="Customer Growth" icon={TrendingUp} className="lg:col-span-2">
          <ChartWrap height={200}>
            <AreaChart data={monthlySignups}>
              <defs>
                <linearGradient id="cGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#7C3AED" stopOpacity={0.18} />
                  <stop offset="100%" stopColor="#7C3AED" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid {...GRID} />
              <XAxis dataKey="m" tick={AXIS} axisLine={false} tickLine={false} />
              <YAxis tick={AXIS} axisLine={false} tickLine={false} width={24} />
              <Tooltip content={<ChartTip />} />
              <Legend wrapperStyle={{ fontSize:10, paddingTop:6 }} />
              <Area type="monotone" dataKey="customers" stroke="#7C3AED" strokeWidth={2.5} fill="url(#cGrad)" name="New Customers" />
            </AreaChart>
          </ChartWrap>
        </ChartCard>

        <ChartCard title="Status Distribution" icon={Users} iconColor="#7C3AED">
          <ChartWrap height={200}>
            <PieChart>
              <Pie data={statusDist} cx="50%" cy="42%" innerRadius={50} outerRadius={74}
                dataKey="value" paddingAngle={3} strokeWidth={0}>
                {statusDist.map((d,i) => <Cell key={i} fill={d.color} />)}
              </Pie>
              <Tooltip />
              <Legend iconSize={8} wrapperStyle={{ fontSize:10.5 }} />
            </PieChart>
          </ChartWrap>
        </ChartCard>
      </div>

      <ChartCard title="Top Cities" icon={BarChart3}>
        <ChartWrap height={170}>
          <BarChart data={cityData}>
            <CartesianGrid {...GRID} />
            <XAxis dataKey="name" tick={AXIS} axisLine={false} tickLine={false} />
            <YAxis tick={AXIS} axisLine={false} tickLine={false} width={24} />
            <Tooltip content={<ChartTip />} />
            <Bar dataKey="value" fill="#7C3AED" radius={[6,6,0,0]} name="Customers" />
          </BarChart>
        </ChartWrap>
      </ChartCard>

      <ChartCard title="Top 10 Customers by Revenue" icon={Star} iconColor="#854D0E">
        <DataTable
          columns={[
            { key:"no",     label:"#" },
            { key:"name",   label:"Customer", bold:true },
            { key:"email",  label:"Email" },
            { key:"phone",  label:"Phone" },
            { key:"orders", label:"Orders" },
            { key:"spent",  label:"Total Spent", right:true, bold:true },
            { key:"status", label:"Status" },
          ]}
          rows={topCustomers.map((c, i) => ({
            no:     i + 1,
            name:   trunc(c.fullName || "—", 16),
            email:  trunc(c.email    || "—", 18),
            phone:  c.phone    || "—",
            orders: c.totalOrders || 0,
            spent:  fmt(c.totalSpent || 0),
            status: <StatusPill status={c.status} />,
          }))}
          emptyMsg="No customer data available"
        />
      </ChartCard>
    </div>
  );
}

// ─── PRODUCTS REPORT ──────────────────────────────────────────────────────────
function ProductsReport({ products, orders, categories }) {
  const totalProducts  = products.length;
  const activeProducts = products.filter(p => p.status === "Active").length;
  const withImages     = products.filter(p => p.images?.length > 0).length;
  const withVariants   = products.filter(p => (p.variations?.length || 0) > 0).length;
  const activeRate     = totalProducts ? Math.round(activeProducts / totalProducts * 100) : 0;
  const imageRate      = totalProducts ? Math.round(withImages / totalProducts * 100) : 0;

  const byCat = useMemo(() => {
    const map = {};
    products.forEach(p => {
      const cat = p.category_name || "Uncategorized";
      if (!map[cat]) map[cat] = { name:cat, total:0, active:0 };
      map[cat].total  += 1;
      if (p.status === "Active") map[cat].active += 1;
    });
    return Object.values(map).sort((a,b) => b.total - a.total).slice(0, 8);
  }, [products]);

  const statusDist = useMemo(() => {
    const map = {};
    products.forEach(p => { map[p.status||"Unknown"] = (map[p.status||"Unknown"] || 0) + 1; });
    return Object.entries(map).map(([name, value]) => ({ name, value }));
  }, [products]);

  const productOrderMap = useMemo(() => {
    const map = {};
    orders.forEach(o => {
      const pn = o.product_name || o.category || "Unknown";
      if (!map[pn]) map[pn] = { name:pn, orders:0, revenue:0 };
      map[pn].orders  += 1;
      map[pn].revenue += o.total || 0;
    });
    return Object.values(map).sort((a,b) => b.revenue - a.revenue).slice(0, 8);
  }, [orders]);

  const AXIS = { fontSize:10, fill:"#9CA3AF" };
  const GRID = { strokeDasharray:"2 8", stroke:"rgba(0,0,0,0.05)", vertical:false };

  return (
    <div className="space-y-4">
      {/* FIX: sm:grid-cols-4 covers 760px viewport */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 w-full">
        <KpiCard label="Total Products"  value={totalProducts}  icon={Package}     iconBg="#FFFBEB" iconColor="#854D0E" />
        <KpiCard label="Active Products" value={activeProducts} icon={CheckCircle} iconBg="#DCFCE7" iconColor="#15803D"
          sub={`${activeRate}% active`} />
        <KpiCard label="With Images"     value={withImages}     icon={Package}     iconBg="#EFF6FF" iconColor="#1D4ED8"
          sub={`${imageRate}% coverage`} />
        <KpiCard label="With Variants"   value={withVariants}   icon={BarChart3}   iconBg="#F3E8FF" iconColor="#6B21A8" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Products by Category" icon={Package} iconColor="#854D0E">
          <ChartWrap height={210}>
            <BarChart data={byCat}>
              <CartesianGrid {...GRID} />
              <XAxis dataKey="name" tick={AXIS} axisLine={false} tickLine={false} angle={-15} textAnchor="end" height={42} />
              <YAxis tick={AXIS} axisLine={false} tickLine={false} width={24} />
              <Tooltip content={<ChartTip />} />
              <Legend wrapperStyle={{ fontSize:10, paddingTop:6 }} />
              <Bar dataKey="total"  fill="#F59E0B" name="Total" />
              <Bar dataKey="active" fill="#10B981" radius={[4,4,0,0]} name="Active" />
            </BarChart>
          </ChartWrap>
        </ChartCard>

        <ChartCard title="Product Status" icon={BarChart3} iconColor="#F59E0B">
          <ChartWrap height={210}>
            <PieChart>
              <Pie data={statusDist} cx="50%" cy="42%" innerRadius={50} outerRadius={74}
                dataKey="value" paddingAngle={3} strokeWidth={0}>
                {statusDist.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip />
              <Legend iconSize={8} wrapperStyle={{ fontSize:10.5 }} />
            </PieChart>
          </ChartWrap>
        </ChartCard>
      </div>

      <ChartCard title="Top Products by Revenue" icon={TrendingUp}>
        <ChartWrap height={190}>
          <BarChart data={productOrderMap} layout="vertical">
            <CartesianGrid strokeDasharray="2 8" stroke="rgba(0,0,0,0.05)" horizontal={false} />
            <XAxis type="number" tick={AXIS} tickFormatter={fmtK} axisLine={false} tickLine={false} />
            <YAxis type="category" dataKey="name" width={96} tick={AXIS} axisLine={false} tickLine={false} />
            <Tooltip content={<ChartTip />} />
            <Bar dataKey="revenue" fill="#F59E0B" radius={[0,6,6,0]} name="Revenue" />
          </BarChart>
        </ChartWrap>
      </ChartCard>

      <ChartCard title="All Products" icon={Package}>
        <DataTable
          columns={[
            { key:"no",       label:"#" },
            { key:"name",     label:"Product",  bold:true },
            { key:"category", label:"Category" },
            { key:"sku",      label:"SKU" },
            { key:"price",    label:"Price",    right:true },
            { key:"mrp",      label:"MRP",      right:true },
            { key:"stock",    label:"Stock",    right:true },
            { key:"status",   label:"Status" },
          ]}
          rows={products.slice(0,15).map((p, i) => ({
            no:       i + 1,
            name:     trunc(p.name, 20),
            category: trunc(p.category_name || "—", 14),
            sku:      trunc(p.sku || "—", 12),
            price:    fmt(p.your_price || 0),
            mrp:      fmt(p.max_retail_price || 0),
            stock:    p.stock ?? "—",
            status:   <StatusPill status={p.status} />,
          }))}
          emptyMsg="No products found"
        />
      </ChartCard>
    </div>
  );
}

// ─── FINANCIAL REPORT ─────────────────────────────────────────────────────────
function FinancialReport({ orders, expenses: expenseList }) {
  const [from, setFrom] = useState("");
  const [to,   setTo]   = useState("");

  const filteredOrders = useMemo(() =>
    orders.filter(o => dateInRange(o.date, from, to)),
  [orders, from, to]);

  const filteredExp = useMemo(() =>
    expenseList.filter(e => dateInRange(e.created_at || e.date, from, to)),
  [expenseList, from, to]);

  const totalRevenue  = filteredOrders.reduce((s, o) => s + (o.total || 0), 0);
  const totalExpenses = filteredExp.reduce((s, e) => s + parseFloat(e.amount || 0), 0);
  const netProfit     = totalRevenue - totalExpenses;
  const margin        = totalRevenue > 0 ? Math.round(netProfit / totalRevenue * 100) : 0;

  const monthlyPL = useMemo(() => {
    const revMap = {};
    filteredOrders.forEach(o => {
      const k = getMonthKey(o.date);
      if (!k) return;
      if (!revMap[k]) revMap[k] = { m: monthLabel(k), rev:0, exp:0, profit:0 };
      revMap[k].rev += o.total || 0;
    });
    filteredExp.forEach(e => {
      const k = getMonthKey(e.created_at || e.date);
      if (!k) return;
      if (!revMap[k]) revMap[k] = { m: monthLabel(k), rev:0, exp:0, profit:0 };
      revMap[k].exp += parseFloat(e.amount || 0);
    });
    return Object.entries(revMap).sort(([a],[b]) => a.localeCompare(b))
      .map(([, v]) => ({ ...v, profit: v.rev - v.exp }));
  }, [filteredOrders, filteredExp]);

  const AXIS = { fontSize:10, fill:"#9CA3AF" };
  const GRID = { strokeDasharray:"2 8", stroke:"rgba(0,0,0,0.05)", vertical:false };

  return (
    <div className="space-y-4">
      <FilterBar from={from} to={to} setFrom={setFrom} setTo={setTo} onReset={() => { setFrom(""); setTo(""); }} />

      {/* FIX: sm:grid-cols-4 covers 760px */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 w-full">
        <KpiCard label="Total Revenue"  value={fmtK(totalRevenue)}  icon={IndianRupee} iconBg="#F0FDFA" iconColor="#0F766E" />
        <KpiCard label="Total Expenses" value={fmtK(totalExpenses)} icon={Receipt}     iconBg="#FEE2E2" iconColor="#991B1B" />
        <KpiCard label="Net Profit"     value={fmtK(netProfit)}     icon={TrendingUp}  iconBg={netProfit>=0?"#DCFCE7":"#FEE2E2"}
          iconColor={netProfit>=0?"#15803D":"#991B1B"} trend={margin} trendLabel={`${margin}% margin`} />
        <KpiCard label="Profit Margin"  value={`${margin}%`}        icon={BarChart3}   iconBg="#EFF6FF" iconColor="#1D4ED8" />
      </div>

      <ChartCard title="Monthly Profit & Loss" icon={TrendingUp}>
        <ChartWrap height={230}>
          <ComposedChart data={monthlyPL}>
            <CartesianGrid {...GRID} />
            <XAxis dataKey="m" tick={AXIS} axisLine={false} tickLine={false} />
            <YAxis tick={AXIS} tickFormatter={fmtK} width={48} axisLine={false} tickLine={false} />
            <Tooltip content={<ChartTip />} />
            <Legend wrapperStyle={{ fontSize:10, paddingTop:6 }} />
            <Bar dataKey="rev"    fill="#0F766E" radius={[4,4,0,0]} name="Revenue" />
            <Bar dataKey="exp"    fill="#EF4444" radius={[4,4,0,0]} name="Expenses" />
            <Line type="monotone" dataKey="profit" stroke="#10B981" strokeWidth={2.5}
              dot={{ r:4, fill:"#10B981", strokeWidth:2, stroke:"#fff" }} name="Net Profit" />
          </ComposedChart>
        </ChartWrap>
      </ChartCard>

      <ChartCard title="P&L Summary by Month" icon={FileText}>
        <DataTable
          columns={[
            { key:"m",      label:"Month" },
            { key:"rev",    label:"Revenue",    right:true, bold:true },
            { key:"exp",    label:"Expenses",   right:true },
            { key:"profit", label:"Net Profit", right:true, bold:true },
            { key:"margin", label:"Margin",     right:true },
          ]}
          rows={monthlyPL.map(r => ({
            m:      r.m,
            rev:    fmt(r.rev),
            exp:    fmt(r.exp),
            profit: <span style={{ color: r.profit >= 0 ? "#15803D" : "#991B1B", fontWeight:700 }}>{fmt(r.profit)}</span>,
            margin: `${r.rev > 0 ? Math.round(r.profit/r.rev*100) : 0}%`,
          }))}
          emptyMsg="No financial data for this period"
        />
      </ChartCard>
    </div>
  );
}

// ─── EXPENSE REPORT ───────────────────────────────────────────────────────────
function ExpenseReport({ expenses: expenseList }) {
  const [from, setFrom] = useState("");
  const [to,   setTo]   = useState("");
  const [catF, setCatF] = useState("all");

  const filtered = useMemo(() =>
    expenseList.filter(e => {
      const inRange = dateInRange(e.created_at || e.date, from, to);
      const matchC  = catF === "all" || (e.name||"").toLowerCase() === catF;
      return inRange && matchC;
    }),
  [expenseList, from, to, catF]);

const totalExp = filtered.reduce(
  (s, e) => s + (parseFloat(e.expense_amount) || 0),
  0
);
const avgExp = filtered.length ? totalExp / filtered.length : 0;

const highestEntry = filtered.reduce(
  (max, e) =>
    parseFloat(e.expense_amount || 0) >
    parseFloat(max?.expense_amount || 0)
      ? e
      : max,
  null
);
  

  const catData = useMemo(() => {
    const map = {};
    filtered.forEach(e => {
      const cat = e.name || "Other";
      map[cat] = (map[cat] || 0) + parseFloat(e.amount || 0);
    });
    return Object.entries(map).map(([name, value]) => ({ name, value: Math.round(value) }))
      .sort((a,b) => b.value - a.value);
  }, [filtered]);

const monthlyExp = useMemo(() => {
  const map = {};
  filtered.forEach(e => {
     const k = getMonthKey(e.expense_date);
    if (!k) return;

const amt = parseFloat(e.expense_amount) || 0;
    if (!map[k]) map[k] = { m: monthLabel(k), amount: 0, count: 0 };
    map[k].amount += amt;
    map[k].count  += 1;
  });
  return Object.entries(map).sort(([a],[b]) => a.localeCompare(b)).map(([,v]) => v);
}, [filtered]);

  const uniqueCats = ["all", ...Array.from(new Set(expenseList.map(e => (e.name||"").toLowerCase()).filter(Boolean)))];

  const AXIS = { fontSize:10, fill:"#9CA3AF" };
  const GRID = { strokeDasharray:"2 8", stroke:"rgba(0,0,0,0.05)", vertical:false };
console.log(expenseList[0]);
console.log(Object.keys(expenseList[0]));
console.log('expenseList:', expenseList);
console.log('filtered:', filtered);
console.log('catData:', catData);
console.log('monthlyExp:', monthlyExp);
  return (
    <div className="space-y-4">
      <FilterBar from={from} to={to} setFrom={setFrom} setTo={setTo} onReset={() => { setFrom(""); setTo(""); setCatF("all"); }}
        extra={
          <select value={catF} onChange={e => setCatF(e.target.value)}
            className="border border-gray-200 rounded-lg px-2 py-1 text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-500 capitalize w-full">
            {uniqueCats.map(s => <option key={s} value={s}>{s === "all" ? "All Categories" : s}</option>)}
          </select>
        }
      />

      {/* FIX: sm:grid-cols-4 covers 760px viewport */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 w-full">
        <KpiCard label="Total Expenses"  value={fmtK(totalExp)}                  icon={Receipt}     iconBg="#FEE2E2" iconColor="#991B1B" />
        <KpiCard label="Entries"         value={filtered.length}                 icon={FileText}    iconBg="#F3F4F6" iconColor="#4B5563" />
        <KpiCard label="Avg Expense"     value={fmtK(avgExp)}                    icon={BarChart3}   iconBg="#EFF6FF" iconColor="#1D4ED8" />
        <KpiCard label="Highest Single"  value={fmtK(highestEntry?.expense_amount || 0)}
 icon={AlertCircle} iconBg="#FEF9C3" iconColor="#854D0E"
       sub={highestEntry?.expense_name || highestEntry?.name} />
      </div>

      {/* FIX: sm:grid-cols-3 + sm:col-span-2 so charts go side-by-side at 760px */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <ChartCard title="Monthly Expense Trend" icon={TrendingUp} iconColor="#EF4444" className="sm:col-span-2">
          <ChartWrap height={200}>
            <AreaChart data={monthlyExp}>
              <defs>
                <linearGradient id="expGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#EF4444" stopOpacity={0.18} />
                  <stop offset="100%" stopColor="#EF4444" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid {...GRID} />
              <XAxis dataKey="m" tick={AXIS} axisLine={false} tickLine={false} />
              <YAxis tick={AXIS} tickFormatter={fmtK} width={48} axisLine={false} tickLine={false} />
              <Tooltip content={<ChartTip />} />
              <Area type="monotone" dataKey="amount" stroke="#EF4444" strokeWidth={2.5} fill="url(#expGrad)" name="Expenses" />
            </AreaChart>
          </ChartWrap>
        </ChartCard>

        <ChartCard title="By Category" icon={Receipt} iconColor="#EF4444">
          <ChartWrap height={200}>
            <PieChart>
              <Pie data={catData} cx="50%" cy="42%" innerRadius={48} outerRadius={72}
                dataKey="value" paddingAngle={3} strokeWidth={0}>
                {catData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={v => fmtK(v)} />
              <Legend iconSize={8} wrapperStyle={{ fontSize:10 }} />
            </PieChart>
          </ChartWrap>
        </ChartCard>
      </div>

      <ChartCard title="Category Breakdown" icon={BarChart3}>
        <ChartWrap height={190}>
          <BarChart data={catData} layout="vertical">
            <CartesianGrid strokeDasharray="2 8" stroke="rgba(0,0,0,0.05)" horizontal={false} />
            <XAxis type="number" tick={AXIS} tickFormatter={fmtK} axisLine={false} tickLine={false} />
            <YAxis type="category" dataKey="name" width={96} tick={AXIS} axisLine={false} tickLine={false} />
            <Tooltip content={<ChartTip />} />
            <Bar dataKey="value" fill="#EF4444" radius={[0,6,6,0]} name="Amount" />
          </BarChart>
        </ChartWrap>
      </ChartCard>

      <ChartCard title="Expense Entries" icon={FileText}>
        <DataTable
          columns={[
            { key:"no",     label:"#" },
            { key:"name",   label:"Category", bold:true },
            { key:"desc",   label:"Description" },
            { key:"date",   label:"Date" },
            { key:"amount", label:"Amount", right:true, bold:true },
          ]}
          rows={filtered.slice(0,15).map((e, i) => ({
            no:     i + 1,
           
            desc:   trunc(e.description || "—", 20),
           name: e.expense_name || e.name,
date: e.expense_date?.slice(0, 10),
amount: fmt(parseFloat(e.expense_amount || 0)),
          }))}
          emptyMsg="No expenses in this period"
        />
      </ChartCard>
    </div>
  );
}

// ─── Main Hub ─────────────────────────────────────────────────────────────────
export default function ReportsHub() {
  const [activeTab, setActiveTab] = useState("dayend");
  const { orders    = [], fetchOrders   } = useOrders()    || {};
  const { customers = [] }               = useCustomers() || {};
  const { products  = [] }               = useProducts()  || {};
const { expenses  = [], fetchExpenses } = useExpenses()  || {};
useEffect(() => { fetchOrders?.(); fetchExpenses?.(); }, []);
  const { categories = [] }              = useCategories()|| {};

  useEffect(() => { fetchOrders?.(); }, []); // eslint-disable-line

  return (
    <div className="min-h-screen bg-gray-50">

      {/* Page header */}
      <div className="bg-white border-b border-gray-200 px-4 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 bg-gradient-to-br from-teal-600 to-teal-800 rounded-xl flex items-center justify-center shadow flex-shrink-0">
            <BarChart3 className="w-4 h-4 text-white" />
          </div>
          <div>
            <h1 className="text-base font-extrabold text-gray-900 leading-tight">Business Reports</h1>
            <p className="text-[10px] text-gray-400 mt-0.5 hidden sm:block">Analytics & insights for better decisions</p>
          </div>
        </div>
      </div>

      {/* Tab bar */}
      <div className="bg-white border-b border-gray-200 px-1 sm:px-2 md:px-4">
        <div className="flex flex-wrap">
          {REPORT_TABS.map(tab => {
            const Icon = tab.icon;
            const isAct = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className="flex items-center gap-1 px-2 sm:px-3 py-2.5 text-[10px] sm:text-xs font-semibold transition-all whitespace-nowrap border-b-2"
                style={{
                  color:       isAct ? tab.color : "#9CA3AF",
                  borderColor: isAct ? tab.color : "transparent",
                  background:  isAct ? `${tab.color}0D` : "transparent",
                }}
              >
                <Icon className="w-3 h-3 sm:w-3.5 sm:h-3.5 flex-shrink-0 hidden xs:block" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div className="p-2.5 sm:p-3 md:p-5">
        {activeTab === "dayend"    && <DayEndReport    orders={orders} />}
        {activeTab === "sales"     && <SalesReport     orders={orders} />}
        {activeTab === "orders"    && <OrdersReport    orders={orders} />}
        {activeTab === "customers" && <CustomersReport customers={customers} orders={orders} />}
        {activeTab === "products"  && <ProductsReport  products={products} orders={orders} categories={categories} />}
        {activeTab === "financial" && <FinancialReport orders={orders} expenses={expenses} />}
        {activeTab === "expenses"  && <ExpenseReport   expenses={expenses} />}
      </div>
    </div>
  );
}